import React, { useState, useEffect } from 'react';
import { dashboardApi, EspConfig } from '../services/dashboardApi';
import { FileText, AlertCircle, CheckCircle, Upload } from 'lucide-react';

export default function SimplifiedModePanel({
    esp,
    onConfigChange,
}: {
    esp: EspConfig;
    onConfigChange: (updatedEsp: EspConfig) => void;
}) {
    const [config, setConfig] = useState({
        suspendAfterDays: esp.suspendAfterDays ?? 14,
        deleteAfterDays: esp.deleteAfterDays ?? 30,
        dailyIntakeMin: esp.dailyIntakeMin ?? 300,
        dailyIntakeMax: esp.dailyIntakeMax ?? 600,
        accountCap: esp.accountCap ?? 100000,
        priorityTags: esp.priorityTags?.join(', ') ?? '',
        prioritySuppliers: esp.prioritySuppliers?.join(', ') ?? '',
    });
    const [saving, setSaving] = useState(false);
    const [queueStatus, setQueueStatus] = useState<any>(null);

    // CSV upload state
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    useEffect(() => {
        if (esp.simplifiedMode) {
            fetchQueueStatus();
        }
    }, [esp.simplifiedMode, esp.id]);

    const fetchQueueStatus = async () => {
        try {
            const res = await dashboardApi.getPriorityQueueStatus(esp.id);
            setQueueStatus(res);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            const payload = {
                simplifiedMode: esp.simplifiedMode,
                suspendAfterDays: config.suspendAfterDays,
                deleteAfterDays: config.deleteAfterDays,
                dailyIntakeMin: config.dailyIntakeMin,
                dailyIntakeMax: config.dailyIntakeMax,
                accountCap: config.accountCap,
                priorityTags: config.priorityTags.split(',').map(s => s.trim()).filter(Boolean),
                prioritySuppliers: config.prioritySuppliers.split(',').map(s => s.trim()).filter(Boolean),
            };
            await dashboardApi.updateSimplifiedConfig(esp.id, payload);
            onConfigChange({ ...esp, ...payload });
            alert('Saved successfully!');
        } catch (err) {
            alert('Failed to save config');
        } finally {
            setSaving(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.name.endsWith('.csv')) {
            setFile(droppedFile);
            setUploadError(null);
        } else {
            setUploadError('Please upload a CSV file');
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setUploadError(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        setUploadResult(null);

        try {
            const text = await file.text();
            const lines = text.split(/\r?\n/).filter(line => line.trim());
            if (lines.length < 2) throw new Error('CSV is empty or missing data');
            
            const header = lines[0].toLowerCase();
            const delimiter = header.includes(';') ? ';' : ',';
            const headers = header.split(delimiter).map(h => h.trim());
            const emailIdx = headers.indexOf('email');
            
            if (emailIdx === -1) throw new Error('CSV must have an "email" column');

            const emails: string[] = [];
            for (let i = 1; i < lines.length; i++) {
                const cols = lines[i].split(delimiter);
                const email = cols[emailIdx]?.trim();
                if (email && email.includes('@')) {
                    emails.push(email);
                }
            }

            if (emails.length === 0) throw new Error('No valid emails found');

            await dashboardApi.addToPriorityQueue(esp.id, emails, 'csv_upload');
            setUploadResult(`Successfully queued ${emails.length} emails`);
            setFile(null);
            fetchQueueStatus();
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleClearQueue = async () => {
        if (!window.confirm('Clear the priority queue?')) return;
        try {
            await dashboardApi.clearPriorityQueue(esp.id);
            fetchQueueStatus();
        } catch (err) {
            alert('Failed to clear queue');
        }
    };

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-lg p-5 shadow-inner">
            {/* Config Panel */}
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 border-b pb-2">Simplified Configuration</h3>
                
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Suspend after days</label>
                        <input type="number" value={config.suspendAfterDays} onChange={e => setConfig({...config, suspendAfterDays: +e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Delete after days</label>
                        <input type="number" value={config.deleteAfterDays} onChange={e => setConfig({...config, deleteAfterDays: +e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Daily Intake Min</label>
                        <input type="number" value={config.dailyIntakeMin} onChange={e => setConfig({...config, dailyIntakeMin: +e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Daily Intake Max</label>
                        <input type="number" value={config.dailyIntakeMax} onChange={e => setConfig({...config, dailyIntakeMax: +e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Account Cap</label>
                        <input type="number" value={config.accountCap} onChange={e => setConfig({...config, accountCap: +e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Priority Tags (comma-separated)</label>
                    <input type="text" value={config.priorityTags} onChange={e => setConfig({...config, priorityTags: e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="e.g. clicker, opener" />
                </div>
                
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Priority Suppliers (comma-separated)</label>
                    <input type="text" value={config.prioritySuppliers} onChange={e => setConfig({...config, prioritySuppliers: e.target.value})} className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="e.g. sup1, sup2" />
                </div>

                <button onClick={handleSaveConfig} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm">
                    {saving ? 'Saving...' : 'Save Configuration'}
                </button>
            </div>

            {/* Priority Queue Panel */}
            <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="text-sm font-semibold text-gray-800">Priority Queue</h3>
                    {queueStatus && (
                        <div className="text-xs text-gray-500 flex items-center gap-3">
                            <span className="bg-gray-100 px-2 py-0.5 rounded">Pending: <strong className="text-gray-800">{queueStatus.pending_count || 0}</strong></span>
                            <span className="bg-gray-100 px-2 py-0.5 rounded">Processed: <strong className="text-gray-800">{queueStatus.processed_count || 0}</strong></span>
                            <button onClick={handleClearQueue} className="text-red-500 hover:text-red-700 font-medium bg-red-50 px-2 py-0.5 rounded">Clear</button>
                        </div>
                    )}
                </div>

                <div 
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${isDragging ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-gray-300 hover:border-gray-400 bg-white'}`}
                >
                    <FileText className={`w-8 h-8 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className="text-sm text-gray-600 mb-3 font-medium">Drag & drop CSV here</p>
                    <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" id={`file-${esp.id}`} />
                    <label htmlFor={`file-${esp.id}`} className="inline-block px-4 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded border cursor-pointer hover:bg-gray-200 transition-colors">
                        Browse Files
                    </label>
                </div>

                {file && (
                    <div className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded text-xs shadow-sm">
                        <span className="font-medium text-green-900 truncate max-w-[200px]">{file.name}</span>
                        <button onClick={() => setFile(null)} className="text-green-700 hover:text-green-900 font-bold px-1.5 py-0.5 rounded hover:bg-green-100 transition-colors">×</button>
                    </div>
                )}

                {uploadError && (
                    <div className="flex items-center gap-1.5 p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-900 shadow-sm">
                        <AlertCircle className="w-4 h-4 text-red-600 shrink-0" /> <span>{uploadError}</span>
                    </div>
                )}

                {uploadResult && (
                    <div className="flex items-center gap-1.5 p-2.5 bg-green-50 border border-green-200 rounded text-xs text-green-900 shadow-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" /> <span>{uploadResult}</span>
                    </div>
                )}

                <button 
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white text-sm font-medium rounded hover:bg-gray-900 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Processing...' : 'Upload to Priority Queue'}
                </button>
            </div>
        </div>
    );
}
